import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import FilterIcons from '@/icons/FilterIcons'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    ConversionRateStatsPayload,
    getConversionRateStats,
} from '../../../services'
import { FunnelChartSkeleton } from './skeletons'

interface LeadConversionFunnelChartProps {
    filterPayload: ConversionRateStatsPayload
}

interface FunnelData {
    name: string
    value: number
}

// Curved Funnel Layer Component - Hourglass style (same as customer funnel)
interface CurvedFunnelLayerProps {
    topWidth: number
    bottomWidth: number
    height: number
    y: number
    totalWidth: number
    color: string
    index: number
    value: number
}

const CurvedFunnelLayer: React.FC<CurvedFunnelLayerProps> = ({
    topWidth,
    bottomWidth,
    height,
    y,
    totalWidth,
    color,
    index,
    value,
}) => {
    const centerX = totalWidth / 2
    const topLeft = centerX - topWidth / 2
    const topRight = centerX + topWidth / 2
    const bottomLeft = centerX - bottomWidth / 2
    const bottomRight = centerX + bottomWidth / 2

    // Control points for bezier curves (create smooth hourglass effect)
    const curveIntensity = 0.5
    const midY = y + height / 2

    // Create path with bezier curves for smooth tapered edges
    const path = `
        M ${topLeft} ${y}
        L ${topRight} ${y}
        C ${topRight + (bottomRight - topRight) * curveIntensity} ${midY},
          ${bottomRight - (bottomRight - topRight) * curveIntensity} ${midY},
          ${bottomRight} ${y + height}
        L ${bottomLeft} ${y + height}
        C ${bottomLeft + (topLeft - bottomLeft) * curveIntensity} ${midY},
          ${topLeft - (topLeft - bottomLeft) * curveIntensity} ${midY},
          ${topLeft} ${y}
        Z
    `

    const isZero = value === 0
    const patternId = `lead-stripe-pattern-${index}`

    return (
        <>
            {isZero && (
                <defs>
                    <pattern
                        id={patternId}
                        patternUnits="userSpaceOnUse"
                        width="8"
                        height="8"
                        patternTransform="rotate(45)"
                    >
                        <rect width="8" height="8" fill="#F3F8FF" />
                        <line
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="8"
                            stroke="#E6F0FF"
                            strokeWidth="4"
                        />
                    </pattern>
                </defs>
            )}
            <path
                d={path}
                fill={isZero ? `url(#${patternId})` : color}
                className="transition-opacity hover:opacity-90"
            />
        </>
    )
}

const LeadConversionFunnelChart: React.FC<LeadConversionFunnelChartProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation()

    // Fetch conversion rate stats
    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'conversion-rate-stats',
                filterPayload,
            ],
            queryFn: () => getConversionRateStats(filterPayload),
        },
        { loadingComponent: <FunnelChartSkeleton /> }
    )

    // Funnel data: Leads đổ về -> Chưa phân loại -> Đã phân loại -> Warm/SQL/Cool/Unqualified
    const funnelData: FunnelData[] = useMemo(() => {
        if (!data?.result) return []
        const stats = data.result

        return [
            { name: t('leads-incoming'), value: stats.total_leads ?? 0 },
            { name: t('unclassified'), value: stats.unclassified ?? 0 },
            { name: t('total-classified'), value: stats.total_classified ?? 0 },
            { name: t('warm-lead'), value: stats.warm_lead ?? 0 },
            {
                name: t('sale-qualified-lead'),
                value: stats.sale_qualified_lead ?? 0,
            },
            { name: t('cool-lead'), value: stats.cool_lead ?? 0 },
            { name: t('unqualified'), value: stats.un_qualified_lead ?? 0 },
        ]
    }, [data, t])

    // Funnel configuration - width proportional to actual values
    const funnelConfig = useMemo(() => {
        const svgWidth = 400
        const svgHeight = 420
        const layerCount = funnelData.length
        const layerHeight = svgHeight / layerCount
        const maxWidth = 380
        const minWidth = 80

        // Find max value for scaling
        const maxValue = Math.max(...funnelData.map((d) => d.value), 1)

        // Light blue gradient colors (same as customer funnel)
        const colors = [
            '#C1DBFF',
            '#FFE4C4',
            '#CDE2FF',
            '#D3E6FF',
            '#D4EDDA', // SQL - light green
            '#E6F0FF',
            '#EFF6FF',
        ]

        // Calculate widths based on actual values
        const widths = funnelData.map((item, index) => {
            if (item.value === 0) {
                const baseRatio = 0.3 - index * 0.04
                return (
                    minWidth + (maxWidth - minWidth) * Math.max(baseRatio, 0.1)
                )
            }
            const ratio = item.value / maxValue
            return minWidth + (maxWidth - minWidth) * ratio
        })

        return funnelData.map((item, index) => ({
            ...item,
            topWidth: widths[index],
            bottomWidth: widths[index + 1] ?? widths[index] * 0.8,
            height: layerHeight,
            y: index * layerHeight,
            totalWidth: svgWidth,
            color: colors[index] ?? '#F3F8FF',
        }))
    }, [funnelData])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const stats = data?.result
    if (!stats) return null

    return (
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full">
            <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-2">
                    <FilterIcons className="text-primary w-4 h-4" />
                    <Typogaphy className="font-medium text-primary capitalize">
                        {t('lead-conversion-funnel')}
                    </Typogaphy>
                </div>
            </div>

            {/* Curved Funnel Chart with labels on right */}
            <div className="flex items-center justify-center">
                {/* Funnel SVG */}
                <div className="flex-1 flex justify-center">
                    <svg
                        width="100%"
                        height="440"
                        viewBox="0 0 400 420"
                        className="overflow-visible max-w-[450px]"
                    >
                        {funnelConfig.map((layer, index) => (
                            <CurvedFunnelLayer
                                key={index}
                                index={index}
                                topWidth={layer.topWidth}
                                bottomWidth={layer.bottomWidth}
                                height={layer.height}
                                y={layer.y}
                                totalWidth={layer.totalWidth}
                                color={layer.color}
                                value={layer.value}
                            />
                        ))}
                    </svg>
                </div>

                {/* Labels on right side */}
                <div className="flex flex-col justify-between h-[440px] py-4 w-[180px] flex-shrink-0">
                    {funnelData?.length > 0 &&
                        funnelData?.map((item, index) => (
                            <div key={index} className="flex flex-col">
                                <Typogaphy className="text-xs text-gray-500 capitalize">
                                    {item.name}
                                </Typogaphy>
                                <Typogaphy
                                    className={`text-2xl font-bold ${item.value === 0 ? 'text-gray-400' : 'text-[#1a237e]'}`}
                                >
                                    {item.value === 0
                                        ? '-'
                                        : item.value.toLocaleString()}
                                </Typogaphy>
                            </div>
                        ))}
                </div>
            </div>

            {/* Summary Stats */}
            <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-blue-50 rounded-lg p-2">
                        <Typogaphy className="text-base font-bold text-blue-600">
                            {(stats.total_leads ?? 0).toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-[10px] text-gray-500 capitalize">
                            {t('total-leads')}
                        </Typogaphy>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-2">
                        <Typogaphy className="text-base font-bold text-orange-600">
                            {(stats.unclassified ?? 0).toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-[10px] text-gray-500 capitalize">
                            {t('unclassified')}
                        </Typogaphy>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                        <Typogaphy className="text-base font-bold text-green-600">
                            {(stats.total_classified ?? 0).toLocaleString()}
                        </Typogaphy>
                        <Typogaphy className="text-[10px] text-gray-500 capitalize">
                            {t('total-classified')}
                        </Typogaphy>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2">
                        <Typogaphy className="text-base font-bold text-amber-600">
                            {(stats.classification_rate ?? 0).toFixed(1)}%
                        </Typogaphy>
                        <Typogaphy className="text-[10px] text-gray-500 capitalize">
                            {t('classification-rate')}
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LeadConversionFunnelChart
